import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import './styles.scss';

const DEFAULT_MAX_MENU_HEIGHT = 240;

const getOptionKey = (option, index) => `${option.value ?? 'empty'}-${index}`;

const normalizeText = (value) => (value ?? '').toString();

const OptimizedCombobox = ({
    id,
    name,
    label,
    options = [],
    value = '',
    onChange,
    placeholder = 'Seleccionar...',
    isDisabled = false,
    isSearchable = true,
    className = '',
    useFixedPosition = false,
    maxMenuHeight = DEFAULT_MAX_MENU_HEIGHT,
    noOptionsMessage = 'No hay opciones disponibles',
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const listboxId = `${id}-listbox`;

    const normalizedOptions = useMemo(() => (
        options.map(option => ({
            ...option,
            value: option.value ?? '',
            label: normalizeText(option.label)
        }))
    ), [options]);

    const selectedOption = useMemo(() =>
        normalizedOptions.find(option => option.value?.toString() === value?.toString()) || null,
        [normalizedOptions, value]
    );

    const displayValue = useMemo(() =>
        selectedOption ? selectedOption.label : normalizeText(value),
        [selectedOption, value]
    );

    const inputValue = useMemo(() =>
        isOpen && isSearchable ? searchTerm : displayValue,
        [isOpen, isSearchable, searchTerm, displayValue]
    );

    const filteredOptions = useMemo(() => {
        if (!isSearchable || !searchTerm.trim()) {
            return normalizedOptions;
        }

        const normalizedSearch = searchTerm.trim().toLowerCase();
        return normalizedOptions.filter(option =>
            option.label.toLowerCase().includes(normalizedSearch)
        );
    }, [normalizedOptions, isSearchable, searchTerm]);

    const updateDropdownPosition = useCallback(() => {
        if (!useFixedPosition || !wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();
        const gap = 4;
        const spaceBelow = window.innerHeight - rect.bottom - gap;
        const spaceAbove = rect.top - gap;
        const opensUp = spaceBelow < 160 && spaceAbove > spaceBelow;
        const availableHeight = Math.max(120, opensUp ? spaceAbove : spaceBelow);
        const menuHeight = Math.min(maxMenuHeight, availableHeight);

        setDropdownStyle({
            position: 'fixed',
            top: opensUp ? rect.top - menuHeight - gap : rect.bottom + gap,
            left: rect.left,
            width: rect.width,
            maxHeight: menuHeight,
            zIndex: 1060
        });
    }, [maxMenuHeight, useFixedPosition]);

    useLayoutEffect(() => {
        if (isOpen) {
            updateDropdownPosition();
        }
    }, [isOpen, filteredOptions.length, updateDropdownPosition]);

    useEffect(() => {
        if (!isOpen || !useFixedPosition) return undefined;

        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);

        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [isOpen, updateDropdownPosition, useFixedPosition]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleClickOutside = (event) => {
            const clickedWrapper = wrapperRef.current?.contains(event.target);
            const clickedDropdown = dropdownRef.current?.contains(event.target);

            if (!clickedWrapper && !clickedDropdown) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (isDisabled) {
            setIsOpen(false);
            setSearchTerm('');
            setHighlightedIndex(-1);
        }
    }, [isDisabled]);

    useEffect(() => {
        setHighlightedIndex(prev => (
            prev >= filteredOptions.length ? filteredOptions.length - 1 : prev
        ));
    }, [filteredOptions.length]);

    const emitChange = useCallback((option) => {
        const fieldName = name || id;
        const syntheticEvent = {
            id: fieldName,
            name: fieldName,
            target: {
                id: fieldName,
                name: fieldName,
                value: option.value
            }
        };

        onChange?.(option, syntheticEvent);
    }, [id, name, onChange]);

    const handleSelectOption = useCallback((option) => {
        if (isDisabled) return;

        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        emitChange(option);
        inputRef.current?.focus();
    }, [emitChange, isDisabled]);

    const handleInputChange = useCallback((event) => {
        if (isDisabled || !isSearchable) return;

        setSearchTerm(event.target.value);
        setHighlightedIndex(-1);
        setIsOpen(true);
    }, [isDisabled, isSearchable]);

    const openMenu = useCallback(() => {
        if (isDisabled) return;

        setIsOpen(true);
        setHighlightedIndex(prev => (prev >= 0 ? prev : 0));
    }, [isDisabled]);

    const closeMenu = useCallback(() => {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
    }, []);

    const handleKeyDown = useCallback((event) => {
        if (isDisabled) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (!isOpen) {
                    openMenu();
                    return;
                }
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
                break;

            case 'ArrowUp':
                event.preventDefault();
                if (!isOpen) {
                    openMenu();
                    return;
                }
                setHighlightedIndex(prev =>
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
                break;

            case 'Enter':
                if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    event.preventDefault();
                    handleSelectOption(filteredOptions[highlightedIndex]);
                } else if (!isOpen) {
                    event.preventDefault();
                    openMenu();
                }
                break;

            case 'Escape':
                event.preventDefault();
                closeMenu();
                inputRef.current?.blur();
                break;

            case 'Tab':
                closeMenu();
                break;

            default:
                break;
        }
    }, [
        closeMenu,
        filteredOptions,
        handleSelectOption,
        highlightedIndex,
        isDisabled,
        isOpen,
        openMenu
    ]);

    const handleInputFocus = useCallback(() => {
        openMenu();
    }, [openMenu]);

    const handleInputClick = useCallback(() => {
        openMenu();
    }, [openMenu]);

    const handleArrowMouseDown = useCallback((event) => {
        event.preventDefault();
        if (isDisabled) return;

        setSearchTerm('');
        setHighlightedIndex(-1);
        setIsOpen(prev => !prev);
        inputRef.current?.focus();
    }, [isDisabled]);

    const renderDropdown = () => (
        <div
            ref={dropdownRef}
            id={listboxId}
            className="combobox-dropdown"
            role="listbox"
            style={useFixedPosition ? dropdownStyle : { maxHeight: maxMenuHeight }}
        >
            {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                    const isSelected = option.value?.toString() === value?.toString();
                    const isHighlighted = index === highlightedIndex;

                    return (
                        <div
                            key={getOptionKey(option, index)}
                            id={`${listboxId}-option-${index}`}
                            className={`combobox-option ${isHighlighted ? 'highlighted' : ''} ${isSelected ? 'selected' : ''}`}
                            role="option"
                            aria-selected={isSelected}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleSelectOption(option)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                        >
                            {option.label}
                        </div>
                    );
                })
            ) : (
                <div className="combobox-no-options">
                    {noOptionsMessage}
                </div>
            )}
        </div>
    );

    const dropdown = isOpen && !isDisabled ? renderDropdown() : null;

    return (
        <div className={`optimized-combobox ${className}`}>
            {label && <label htmlFor={id}>{label}</label>}
            <div className={`combobox-wrapper ${isOpen ? 'is-open' : ''}`} ref={wrapperRef}>
                <input
                    ref={inputRef}
                    id={id}
                    name={name || id}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    onClick={handleInputClick}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    readOnly={!isSearchable}
                    className="combobox-input"
                    autoComplete="off"
                    role="combobox"
                    aria-autocomplete={isSearchable ? 'list' : 'none'}
                    aria-controls={listboxId}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-activedescendant={
                        isOpen && highlightedIndex >= 0
                            ? `${listboxId}-option-${highlightedIndex}`
                            : undefined
                    }
                    {...props}
                />
                <button
                    type="button"
                    className="combobox-arrow"
                    onMouseDown={handleArrowMouseDown}
                    disabled={isDisabled}
                    aria-label={isOpen ? 'Cerrar opciones' : 'Abrir opciones'}
                    tabIndex={-1}
                >
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {!useFixedPosition && dropdown}
            </div>
            {useFixedPosition && dropdown ? createPortal(dropdown, document.body) : null}
        </div>
    );
};

export default OptimizedCombobox;
